import React, { useEffect, useState } from 'react';
import FarmLevelReportTable from './ManageProjectsTable';
import CardDropdown from 'components/common/CardDropdown';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import useApi from 'hooks/useApi';
import {
  getExportAllData,
  getFarmTableData
} from 'api/endpoints/TerrestrialServices';
import { get } from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { isEmpty } from 'lodash';
import { faDownload, faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomSpinner from 'components/common/CustomSpinner';
import { getDistrictData, getStateData } from 'api/endpoints/Location';
import { getWeatherReportdata } from 'api/endpoints/Weather';
import CustomSelect from 'components/common/CustomSelect';
import * as XLSX from 'xlsx';
import MultiSelect from 'components/common/MultiSelectComponent';
import { getTenantFromToken } from 'utils/routeUtils';

function WeatherDashboard() {
  const tenant = getTenantFromToken();
  const getExportAllApi = useApi(getExportAllData);

  const getStateDataApi = useApi(getStateData);
  const getDistrictDataApi = useApi(getDistrictData);
  const getWeatherReportDataApi = useApi(getWeatherReportdata);

  const [stateList, setStateList] = useState(null);
  const [districtList, setDistrictList] = useState(null);
  const [weatherReport, setWeatherReport] = useState(null);

  const [selectedCountry, setSelectedCountry] = useState(1);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [filters, setFilters] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [shouldTrigger, setShouldTrigger] = useState(false);

  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [exportdata, setExportData] = useState({});
  const [exportKey, setExportKey] = useState({});

  useEffect(() => {
    setSelectedCountry(1);
  }, []);

  useEffect(() => {
    setStateList([{ label: 'Loading....', isDisabled: true }]);
    getStateDataApi.request({ countryId: selectedCountry });
  }, [selectedCountry]);

  useEffect(() => {
    const data = get(getStateDataApi, 'data.results', []);
    setStateList(data);
  }, [getStateDataApi.data]);

  useEffect(() => {
    setDistrictList([{ label: 'Loading.....', isDisabled: true }]);
    if (selectedState) {
      getDistrictDataApi.request({ stateId: selectedState.value });
    }
  }, [selectedState]);

  useEffect(() => {
    const data = get(getDistrictDataApi, 'data.results', []);
    setDistrictList(data);
    !isEmpty && handleSearch();
  }, [getDistrictDataApi.data]);

  const handleFilterToggle = () => {
    setShowFilter(!showFilter);
  };

  const handleSearch = e => {
    e && e.preventDefault(); // Check if event is passed (for when manually called)
    let cursor = pageIndex * pageSize;
    let district_ids = selectedDistrict.map(d => d.value); // Extract selected district ids
    const state_ids = selectedState?.value ? [selectedState.value] : [];
    getWeatherReportDataApi.request({
      cursor,
      limit: pageSize,
      filter: filters,
      district_ids,
      state_ids
    });
  };

  useEffect(() => {
    handleSearch(); // Trigger search only if a district is selected
  }, [selectedState, selectedDistrict]);

  useEffect(() => {
    const data = get(getWeatherReportDataApi, 'data.results', []);
    const exportKey = get(
      getWeatherReportDataApi,
      'data.results.export_key',
      ''
    );
    setExportKey(exportKey);
    setWeatherReport(data);
  }, [getWeatherReportDataApi.data]);

  const handleDownload = () => {
    const excelData = convertToExcelData(exportdata); // Convert to Excel data format
    const filename = `WeatherAnalyticsReport.xlsx`; // Excel file extension
    downloadExcel(excelData, filename);
  };

  const convertToExcelData = (data, filename) => {
    if (!data || data.length === 0) return [];
    // Create the report title with timestamp
    const now = new Date();
    const timestamp = now
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');
    const reportTitle = `WeatherAnalyticsReport - Generated on ${timestamp} from the FARMSIO Application`;
    // Extract headers
    const headers = Object.keys(data[0]);
    // Map data rows
    const dataRows = data.map(item => headers.map(header => item[header]));
    // Combine title, blank row, headers, and data rows
    const excelData = [[reportTitle], [], headers, ...dataRows];
    return excelData;
  };

  const downloadExcel = (excelData, filename) => {
    // Create a new worksheet from the excel data
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    // Create a new workbook with the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Weather Analytics Report');
    // Write the Excel file to a Blob and download it
    XLSX.writeFile(wb, filename);
  };

  return (
    <div>
      <div>
        <Form onSubmit={handleSearch} className="mt-1">
          <Row className="g-3 g-lg-4">
            <Col lg={3}>
              <CustomSelect
                options={stateList}
                isClearable={true}
                placeholder="Select State"
                classNamePrefix="react-select"
                value={selectedState}
                onChange={value => {
                  setSelectedState(value);
                  setSelectedDistrict([]);
                }}
              />
            </Col>
            <Col lg={3}>
              <MultiSelect
                options={districtList}
                isClearable={true}
                isDisabled={!selectedState}
                placeholder="Select District"
                classNamePrefix="react-select"
                value={selectedDistrict}
                setSelectedOptions={setSelectedDistrict}
                isMulti={true}
              />
            </Col>
            <Col className="d-flex justify-content-end align-items-start">
              {/* <Button variant="primary" type="submit">
                Search
              </Button> */}
            </Col>
          </Row>
        </Form>
      </div>
      <Row className="mt-4">
        <Col>
          <Card>
            <FalconCardHeader
              title="Weather Forecast"
              titleTag="h5"
              light={true}
              endEl={
                <div className="d-flex justify-content-end my-auto">
                  <div className="px-2 my-auto theme-control-toggle">
                    <CardDropdown
                      onExport={handleDownload}
                      toggleFilters={handleFilterToggle}
                      exportKey={exportKey}
                    />
                  </div>
                </div>
              }
            />
            <CardBody>
              {getWeatherReportDataApi.loading ? (
                <CustomSpinner />
              ) : (
                !isEmpty(weatherReport?.data) && (
                  <FarmLevelReportTable
                    // weatherData={weatherReport}
                    key={'Weather_Dashboard'}
                    hyperlink={false}
                    showFilter={showFilter}
                    selectedDistrict={selectedDistrict}
                    selectedState={selectedState}
                    setExportData={setExportData}
                    showHeader={true}
                    showFooter={true}
                  />
                )
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default WeatherDashboard;
